// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { createDataProvider } from '@shared/ui-kit/providers/data-provider';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import type * as BrokerageTypes from '@shared/data-access/types/brokerage-types';
import produce from 'immer';

export interface SyncState {
  /** Whether a sync is currently in progress */
  isSyncing: boolean;
  /** Current batch ID being synced */
  currentBatchId: string | null;
  /** Current sync status */
  syncStatus: BrokerageTypes.SyncStatus | null;
  /** Error from the last sync operation */
  error: Error | null;
}

export interface StartSyncOptions {
  /**
   * Display name for the institution (used in syncing snackbar when syncing a
   * single institution)
   */
  institutionDisplayName?: string;
}

export interface InstitutionSyncContextValue {
  /** Current sync state */
  syncState: SyncState;
  /** Start a sync operation */
  startSync: (
    params: BrokerageTypes.CreateAdvisoryInstitutionsSyncParams &
      BrokerageTypes.CreateAdvisoryInstitutionsSyncRequestBody,
    institutionId: string,
    institutionName: string,
  ) => Promise<void>;
  /** Cancel current sync operation */
  cancelSync: () => void;
  /** Connection statuses */
  connectionStatuses: Map<string, string | null>;
}

export const useInstitutionSyncFactory = (): InstitutionSyncContextValue => {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [syncState, setSyncState] = useState<SyncState>({
    isSyncing: false,
    currentBatchId: null,
    syncStatus: null,
    error: null,
  });

  const [connectionStatuses, setConnectionStatuses] = useState(
    new Map<string, string | null>(),
  );

  const [syncInstitutions] = advisoryApi.useSyncInstitutionsMutation();
  const [getSyncStatus] =
    advisoryApi.useSyncInstitutionsSyncBatchIdStatusMutation();

  const startSync = useCallback(
    async (
      params: BrokerageTypes.CreateAdvisoryInstitutionsSyncParams &
        BrokerageTypes.CreateAdvisoryInstitutionsSyncRequestBody,
      institutionId: string,
      institutionName: string,
    ) => {
      const isAll = institutionId.toLowerCase() === 'all';

      const syncingMessage = isAll
        ? t('aggregator.syncing', { ns: 'aggregator' })
        : institutionName;

      const syncingKey = enqueueSnackbar(syncingMessage, {
        variant: 'syncing',
        persist: true,
      });

      try {
        setSyncState(prev =>
          produce(prev, draft => {
            draft.isSyncing = true;
            draft.error = null;
          }),
        );

        if (!isAll) {
          setConnectionStatuses(prev =>
            produce(prev, draft => {
              params.connection_ids.forEach(id => {
                draft.set(id.toString(), 'pending');
              });
            }),
          );
        }

        const result = await syncInstitutions(params).unwrap();

        if (!result) {
          throw new Error('Failed to start sync: no result returned');
        }

        setSyncState(prev =>
          produce(prev, draft => {
            draft.currentBatchId = result.batch_id;
          }),
        );

        const status = await getSyncStatus({
          batch_id: result.batch_id,
          client_id: params.client_id,
        }).unwrap();

        setSyncState(prev =>
          produce(prev, draft => {
            draft.isSyncing = false;
            draft.syncStatus = status;
            draft.currentBatchId = status.is_completed ? null : result.batch_id;
          }),
        );

        if (!isAll) {
          setConnectionStatuses(prev =>
            produce(prev, draft => {
              Object.entries(status.connections ?? {}).forEach(
                ([institution_id, connection]) => {
                  draft.set(institution_id, connection.status);
                },
              );
            }),
          );
        }

        closeSnackbar(syncingKey);
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        setSyncState(prev =>
          produce(prev, draft => {
            draft.isSyncing = false;
            draft.error = err;
            draft.currentBatchId = null;
          }),
        );

        if (!isAll) {
          setConnectionStatuses(prev =>
            produce(prev, draft => {
              params.connection_ids.forEach(id => {
                draft.set(id.toString(), 'rejected');
              });
            }),
          );
        }

        closeSnackbar(syncingKey);

        enqueueSnackbar(t('aggregator.sync-failed', { ns: 'aggregator' }), {
          variant: 'syncingFailed',
        });
      }
    },
    [syncInstitutions, getSyncStatus, t, enqueueSnackbar, closeSnackbar],
  );

  const cancelSync = useCallback(() => {
    setSyncState(prev =>
      produce(prev, draft => {
        draft.isSyncing = false;
        draft.currentBatchId = null;
        draft.syncStatus = null;
        draft.error = null;
      }),
    );

    setConnectionStatuses(prev =>
      produce(prev, draft => {
        draft.clear();
      }),
    );
  }, []);

  return useMemo(
    () => ({
      syncState,
      startSync,
      cancelSync,
      connectionStatuses,
    }),
    [syncState, startSync, cancelSync, connectionStatuses],
  );
};

export const {
  useInstitutionSyncData,
  InstitutionSyncProvider,
  InstitutionSyncConsumer,
  InstitutionSyncDataContext,
  withInstitutionSync,
} = createDataProvider({
  name: 'InstitutionSync',
  factory: useInstitutionSyncFactory,
});
